<template>
  <div class="container">
    <div class="row">
      <div class="col-12">
        <TopNav class="mt-4" />
        <img class="mt-3 mb-4" :srcset="`${logo} 3x`" />
        <div class="form-group">
          {{ $t('SETTINGS.TEXT.CONNECT_YOUR_INSTAGRAM_ACCOUNT') }}
        </div>
        <div class="form-group" v-if="instagramConnected">
          <HMActivatedTag :label="$t('SETTINGS.TEXT.CONNECTED_TO_INSTAGRAM')" />
        </div>
        <div v-if="instagramConnected">
          <span v-if="instagramUsername"
            >Connected username:
            <span class="c-charcoal h4">@{{ instagramUsername }}</span></span
          >
          <div class="form-group d-flex align-items-center">
            <div class="mr-3">
              {{ $t('SETTINGS.LABEL.SHOW_FEED') }}
            </div>
            <HMSwitch
              :value="teacher.instagram_feed"
              @input="toggleInstagramFeed"
            />
          </div>
        </div>
      </div>
      <hr class="mx-3 w-100" />
      <div class="col-12 form-group">
        <HMButton
          v-if="instagramConnected"
          color="primary"
          @click="disconnectInstagram"
          :disabled="inProgress"
          :loading="inProgress"
        >
          {{ $t('SETTINGS.BUTTON.DISCONNECT_INSTAGRAM') }}
        </HMButton>
        <HMButton
          v-else
          color="primary"
          @click="authRedirect"
          :disabled="inProgress"
          :loading="inProgress"
        >
          {{ $t('SETTINGS.BUTTON.CONNECT_INSTAGRAM') }}
        </HMButton>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import TopNav from '@/web/components/navigation/TopNav.vue';
import auth from '@/core/api/modules/auth';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';

export default Vue.extend({
  name: 'InstagramIntegration',
  components: {
    TopNav,
  },
  data() {
    return {
      inProgress: false,
      logo: '/assets/images/integrations/instagram@3x.png',
    };
  },
  computed: {
    ...mapGetters('user', ['instagramConnected', 'teacher']),
    instagramUsername() {
      if (!this.teacher.instagram) return null;
      return this.parseUsername(this.teacher.instagram);
    },
  },
  methods: {
    parseUsername(url) {
      let output = url;
      const matches = url.match(
        /(?:https?:\/\/)?(?:www\.)?(?:twitter|medium|facebook|vimeo|instagram)(?:\.com\/)?([@a-zA-Z0-9-_]+)/im
      );
      output = matches?.[1] || output;
      return output;
    },
    notifySuccess() {
      this.$notify({
        group: 'app',
        type: 'success',
        title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
        text: this.$t('SETTINGS.NOTIFICATIONS.INSTAGRAM_SUCCESSFULLY_UPDATED'),
      });
    },
    notifyError(error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: 'Failed to connect Instagram',
        text: errorToMessage(error.response, `Request failed: ${error}`),
        duration: -1, // stay ON
      });
    },
    async authRedirect() {
      const { authorization_url } = await auth.getInstagramRequestAuthURL();
      window.location = authorization_url;
    },
    async connect(code) {
      this.inProgress = true;
      try {
        await auth.connectInstagramAccount(code);
        await this.$store.dispatch('user/loadUserInfo');
        this.notifySuccess();
      } catch (error) {
        this.notifyError(error);
      }
      this.inProgress = false;
    },
    async toggleInstagramFeed() {
      this.inProgress = true;
      try {
        const state = !this.teacher.instagram_feed;
        await this.$store.dispatch('user/toggleInstagramFeed', state);
        this.notifySuccess();
      } catch (error) {
        this.notifyError(error);
      }
      this.inProgress = false;
    },
    async disconnectInstagram() {
      this.inProgress = true;
      try {
        await this.$store.dispatch('user/disconnectInstagram');
        this.notifySuccess();
      } catch (error) {
        this.notifyError(error);
      }
      this.inProgress = false;
    },
  },
  mounted() {
    const code = this.$route.query.code;
    if (code) {
      this.connect(code);
      const query = { ...this.$route.query };
      delete query.code;
      this.$router.replace({ query });
    }
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
</style>
